import React from "react";
import {
  Box,
  useTheme,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Typography,
  Chip,
  Checkbox,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import { tokens } from "../theme";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import {
  CheckBox,
  Delete,
  Edit,
  Email,
  Phone,
  WhatsApp,
} from "@mui/icons-material";
import { useUser } from "../provider/AuthProvider";
import stringToColor from "../services/stringToColor";
import {
  AccountCircle,
  EmailRounded,
  Lock,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import CMTSettingDialog from "../components/CMTSettingDialog";
import ConfirmDialog from "../components/ComfirmationDialog";
import { useTranslation } from "react-i18next";

const CMTDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cmtData, setCmtData] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // or "error"
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [selectedCMT, setSelectedCMT] = useState();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // to track the user being deleted
  const { t } = useTranslation();
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/getCMTList/${user.id}`,
        { withCredentials: true }
      );
      setCmtData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setSnackbarMessage(t("cmtdashboard.error_fetching"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setCurrentUser(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);

      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/deleteCMT/${currentUser}`,
        { withCredentials: true }
      );
      setCmtData((prev) => prev.filter((item) => item.id !== currentUser));
      setSnackbarMessage(t("cmtdashboard.cmt_deleted"));
      setSnackbarSeverity("success");
    } catch (error) {
      console.error("Error deleting CMT:", error);
      setSnackbarMessage(t("cmtdashboard.error_deleting"));
      setSnackbarSeverity("error");
    } finally {
      setIsLoading(false);
      setSnackbarOpen(true);
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setCurrentUser(null); // reset current user after deletion
    }
  };

  const handlePermissionChange = async (cmtId, key, value) => {
    try {
      const cmt = cmtData.find((item) => item.id === cmtId);
      const updatedPermissions = {
        canAddTenantLandlord:
          key === "canAddTenantLandlord"
            ? value
            : cmt.permissions?.canAddTenantLandlord ?? false,
        canAddProvider:
          key === "canAddProvider"
            ? value
            : cmt.permissions?.canAddProvider ?? false,
        canAddUnit:
          key === "canAddUnit" ? value : cmt.permissions?.canAddUnit ?? false,
        canReport:
          key === "canReport" ? value : cmt.permissions?.canReport ?? false,
        canFinancial:
          key === "canFinancial"
            ? value
            : cmt.permissions?.canFinancial ?? false,
        canSubscription:
          key === "canSubscription" ? value : cmt.permissions?.canSubscription ?? false,
        canAccessPortal:
          key === "canAccessPortal"
            ? value
            : cmt.permissions?.canAccessPortal ?? false,
        canAnnouncement:
          key === "canAnnouncement"
            ? value
            : cmt.permissions?.canAnnouncement ?? false,
      };

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/update-permission/${cmtId}`,
        updatedPermissions,
        { withCredentials: true }
      );

      setCmtData((prevData) =>
        prevData.map((item) =>
          item.id === cmtId
            ? {
                ...item,
                permissions: {
                  ...item.permissions,
                  canAddTenantLandlord: updatedPermissions.canAddTenantLandlord,
                  canReport: updatedPermissions.canReport,
                  canAddUnit: updatedPermissions.canAddUnit,
                  canFinancial: updatedPermissions.canFinancial,
                  canAddProvider: updatedPermissions.canAddProvider,
                  canSubscription: updatedPermissions.canSubscription,
                  canAccessPortal: updatedPermissions.canAccessPortal,
                },
              }
            : item
        )
      );
      setSnackbarMessage(t("cmtdashboard.permission_updated", { key }));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      //   fetchData(); // refresh data
    } catch (err) {
      console.error("Permission update failed", err);
      setSnackbarMessage(t("cmtdashboard.error_permission"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const cmtColumns = [
    {
      field: "avatar",
      headerName: t("cmtdashboard.account"),
      flex: 0.5,
      sortable: true,
      filterable: true,
      renderCell: (params) => {
        const name = params?.row?.username || "U";
        const avatarUrl = params?.row?.avatar_url;

        return avatarUrl ? (
          <Box alignItems="center" className="notranslate">
            <Avatar alt={name} src={avatarUrl} />
            <Typography variant="h5" className="notranslate">
              {name}
            </Typography>
          </Box>
        ) : (
          <Box alignItems="center" className="notranslate">
            <Avatar sx={{ bgcolor: stringToColor(name) }}>
              {name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="h5" className="notranslate">
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "contact_information",
      headerName: t("cmtdashboard.contact_info"),
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const contactInfo = params.row.contact_information || {};
        const method = (params?.row?.communication_method || "").toLowerCase();
        const showWhatsApp = method === "whatsapp" || method === "both";
        const showEmail = method === "email" || method === "both";
        return (
          <Box
            display="flex"
            flexDirection="column"
            py={1}
            gap={0.5}
            className="notranslate"
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <Email fontSize="small" />
              <Typography variant="body2">{params.row.email}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Phone fontSize="small" />
              <Typography variant="body2" dir="ltr">
                {params.row.phone}
              </Typography>
            </Box>
            <Box display={"flex"} alignItems="center" gap={0.5}>
              {showWhatsApp && (
                <Box mt={1}>
                  <WhatsApp color="success" fontSize="small" />
                  {/* <Chip label="WhatsApp" color="success" size="small" /> */}
                </Box>
              )}
              {showEmail && (
                <Box mt={1}>
                  <EmailRounded color="info" fontSize="small" />
                  {/* <Chip label="Email" color="info" size="small" /> */}
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
    // {
    //   field: "compound",
    //   headerName: t("cmtdashboard.compound"),
    //   flex: 1,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const compound = params?.row?.compound;
    //     const compoundName = compound?.name || "N/A";
    //     const compoundLogo = compound?.logo_url || null;
    //     const address = compound?.address || "N/A";

    //     return compound ? (
    //       <Box mb={1} className="notranslate">
    //         <Box display="flex" alignItems="center" gap={1}>
    //           {compoundLogo ? (
    //             <Avatar
    //               alt={compoundName}
    //               src={compoundLogo}
    //               className="notranslate"
    //             />
    //           ) : (
    //             <Avatar
    //               sx={{ bgcolor: stringToColor(compoundName) }}
    //               className="notranslate"
    //             >
    //               {compoundName.charAt(0).toUpperCase()}
    //             </Avatar>
    //           )}
    //           <Box>
    //             <Typography
    //               variant="h5"
    //               fontWeight="bold"
    //               className="notranslate"
    //             >
    //               {compoundName}
    //             </Typography>
    //             <Typography variant="h7" className="notranslate">
    //               {address}
    //             </Typography>
    //           </Box>
    //         </Box>
    //       </Box>
    //     ) : (
    //       <Box display="flex" alignItems="center" gap={1}>
    //         <Typography variant="body2">_</Typography>
    //       </Box>
    //     );
    //   },
    // },
    {
      field: "status",
      headerName: t("cmtdashboard.status"),
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          label={
            params?.row?.status === "active"
              ? t("cmtdashboard.active")
              : params?.row?.status === "inactive"
              ? t("cmtdashboard.inactive")
              : t("cmtdashboard.pending")
          }
          color={
            params?.row?.status === "active"
              ? "success"
              : params?.row?.status === "inactive"
              ? "error"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "canAccessPortal",
      headerName: t("cmtdashboard.portal_access"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canAccessPortal || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canAccessPortal",
              e.target.checked
            )
          }
        />
      ),
    },
    {
      field: "canAddTenantLandlord",
      headerName: t("cmtdashboard.tenant_landlord"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canAddTenantLandlord || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canAddTenantLandlord",
              e.target.checked
            )
          }
        />
      ),
    },
    {
      field: "canAddProvider",
      headerName: t("cmtdashboard.add_provider"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canAddProvider || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canAddProvider",
              e.target.checked
            )
          }
        />
      ),
    },
    {
      field: "canAddUnit",
      headerName: t("cmtdashboard.unit"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canAddUnit || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canAddUnit",
              e.target.checked
            )
          }
        />
      ),
    },
    {
      field: "canAnnouncement",
      headerName: t("cmtdashboard.announcement"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canAnnouncement || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canAnnouncement",
              e.target.checked
            )
          }
        />
      ),
    },
    {
      field: "canReport",
      headerName: t("cmtdashboard.report"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canReport || false}
          onChange={(e) =>
            handlePermissionChange(params.row.id, "canReport", e.target.checked)
          }
        />
      ),
    },
    {
      field: "canFinancial",
      headerName: t("cmtdashboard.financial"),
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canFinancial || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canFinancial",
              e.target.checked
            )
          }
        />
      ),
    },

    {
      field: "canSubscription",
      headerName: "Subscription",
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.permissions?.canSubscription || false}
          onChange={(e) =>
            handlePermissionChange(
              params.row.id,
              "canSubscription",
              e.target.checked
            )
          }
        />
      ),
    },

    {
      field: "actions",
      headerName: t("cmtdashboard.actions"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box display="flex" justifyContent="space-between">
          <IconButton
            onClick={() => {
              setSelectedCMT({
                id: params.row.id,
                username: params.row.username,
                email: params.row.email,
                phone: params.row.phone,
                status: params.row.status,
                permissions: {
                  canAddTenantLandlord:
                    params.row.permissions.canAddTenantLandlord,
                  canReport: params.row.permissions.canReport,
                  canAddUnit: params.row.permissions.canAddUnit,
                  canFinancial: params.row.permissions.canFinancial,
                  canSubscription: params.row.permissions.canSubscription,
                  canAddProvider: params.row.permissions.canAddProvider,
                  canAccessPortal: params.row.permissions.canAccessPortal,
                  canAnnouncement: params.row.permissions.canAnnouncement,
                },
              });
              setShowAddDialog(true);
            }}
            color="info"
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => {
              handleDelete(params.row.id);
            }}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];
  return (
    <Box className="main-container mt-4">
      <BreadcrumbsBar />
      <Box mt={3}>
        <Box display="flex" mb={2} justifyContent="space-between">
          <TextField
            label={t("cmtdashboard.search_placeholder")}
            variant="outlined"
            size="small"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedCMT({
                id: null,
                username: null,
                email: null,
                phone: null,
                status: null,
                permissions: {
                  canAddTenantLandlord: false,
                  canAddUnit: false,
                  canReport: false,
                  canFinancial: false,
                  canSubscription: false,
                  canAddProvider: false,
                  canAccessPortal: false,
                  canAnnouncement: false,
                },
              });
              setShowAddDialog(true);
            }}
          >
            {t("cmtdashboard.add_cmt")}
          </Button>
        </Box>
        <Box
          sx={{
            height: "70vh",
          }}
        >
          <DataGrid
            rows={cmtData}
            columns={cmtColumns}
            getRowId={(row) => row.id}
            pageSize={10}
            loading={isLoading}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[5, 10, 15, 20, 25]}
            disableSelectionOnClick
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
              },
              "& .MuiDataGrid-columnHeaders": {
                justifyContent: "start",
                textAlign: "start",
              },
              "& .MuiDataGrid-cell:hover": {
                color: colors.blueAccent[300],
              },
            }}
          />
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <CMTSettingDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        cmt={selectedCMT}
        managerId={user.id}
        onSave={() => {
          fetchData();
          setShowAddDialog(false);
        }}
        onChangeStatus={async (newStatus) => {
          try {
            await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/cmt/userActive`,
              {
                id: selectedCMT.id,
                user_id: user.id,
                status: newStatus,
              },
              { withCredentials: true }
            );
            setCmtData((prev) =>
              prev.map((item) =>
                item.id === selectedCMT.id
                  ? { ...item, status: newStatus }
                  : item
              )
            );

            newStatus == "active"
              ? setSnackbarMessage(t("cmtdashboard.user_activated"))
              : setSnackbarMessage(t("cmtdashboard.user_inactivated"));
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          } catch (err) {
            console.error("Failed to update status", err);
            setSnackbarMessage(t("cmtdashboard.failed_update_status"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        }}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={t("cmtdashboard.confirm_delete_title")}
        content={t("cmtdashboard.confirm_delete_content", {
          username: currentUser?.username,
        })}
        loading={isDeleting}
      />
    </Box>
  );
};

export default CMTDashboard;
